import { useLayoutEffect, useMemo, useRef, useState, type ReactNode, type RefObject } from 'react'
import type { IndexDef, ObjectiveEvent, Snapshot } from '@/api/types/readiness'
import { buildSeries, scaleMax, type Period, type PlanVersionMarker, type SeriesPoint } from '@/lib/readiness/series'
import { assertScale } from '@/lib/readiness/scale'
import { domainColor } from '@/lib/indexes/registry'
import { Icon } from '@/components/readiness/primitives'
import { fmtMonth, projectedScoreAt, PROJECTION_EXPLAINER, type Projection } from '@/lib/readiness/projection'
import { Tooltip } from '@/components/readiness/Term'
import { SvgPill } from '@/components/readiness/annotations'
import { coversPoint, layoutPills, pillSize, type PillSpec } from '@/lib/readiness/annotationLayout'
import { formatDate } from '@/components/common/format'
import { projectionEnd, rangeBounds, type ChartControls, type RangeKey, type SeriesKey } from '@/lib/readiness/chartRange'
export type { ChartControls, RangeKey, SeriesKey } from '@/lib/readiness/chartRange'

// Readiness over time (I2 §7): one card under the page header. Controls row,
// a 150px chart built from the event log bucketed by period, assessment nodes
// as filled circles, the live point hollow, evidence ticks on the baseline,
// a dashed Today marker, the submission / readiness milestone, hover tooltip.
// Clicking an assessment node sets it as the "to" column of the matrix.

const ACCENT = 'var(--rd-accent-primary)'
const SURFACE = 'var(--rd-bg-card)'

const PROJ = 'var(--rd-projection)'
const COV = 'var(--rd-coverage)'

// Assets known over time (I3 §3.2 `Coverage` series) and the scope changes.
export interface CoveragePoint { date: string; count: number }
export interface ScopeMark { date: string; from: number; to: number }

function useWidth<T extends HTMLElement>(fallback = 640): [RefObject<T>, number] {
  const ref = useRef<T>(null)
  const [w, setW] = useState(fallback)
  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return
    const update = () => setW(Math.max(160, Math.floor(el.getBoundingClientRect().width)))
    update()
    if (typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver(update)
    ro.observe(el)
    return () => ro.disconnect()
  }, [])
  return [ref, w]
}

// `compared` rings the From and To points (I6 §2.2); clicking a saved
// assessment dot sets it as From, clicking the Live point sets To = Live.
export function ReadinessOverTime({ index, events, snapshots, planVersions = [], onOpenVersion, controls, onControls, theme, onSelectNode, onSelectLive, compared = null, height = 150, projection = null, coverage = [], scopeMarks = [] }: { index: IndexDef; events: ObjectiveEvent[]; snapshots: Snapshot[]; planVersions?: PlanVersionMarker[]; onOpenVersion?: (n: number) => void; controls: ChartControls; onControls: (c: ChartControls) => void; theme: 'light' | 'dark'; onSelectNode?: (id: string) => void; onSelectLive?: () => void; compared?: { fromId: string | null; toId: string | null } | null; height?: number; projection?: Projection | null; coverage?: CoveragePoint[]; scopeMarks?: ScopeMark[] }) {
  const [ref, width] = useWidth<HTMLDivElement>()
  const [hover, setHover] = useState<{ i: number; x: number; y: number } | null>(null)
  // Plan-version marker tooltip (I5 §9.2): informational, rendered beside the chart.
  const [versionHover, setVersionHover] = useState<{ v: PlanVersionMarker; x: number; y: number } | null>(null)
  const now = useMemo(() => new Date(), [])
  const scale = assertScale(index)
  const sorted = useMemo(() => [...events].sort((a, b) => a.occurred_at.localeCompare(b.occurred_at)), [events])
  const msDate = index.submission?.deadline ?? index.milestone?.date ?? null
  const projTo = projectionEnd(projection, msDate)
  const bounds = rangeBounds(controls, sorted[0]?.occurred_at ?? null, now, projTo)
  const showProjection = !!projection && controls.range === 'est' && controls.series === 'score'
  const series = useMemo(() => buildSeries(index, sorted, snapshots, { period: controls.period, from: bounds.from, to: bounds.to, now }), [index, sorted, snapshots, controls.period, bounds.from, bounds.to, now])
  const max = scaleMax(index)
  const pts = series.points
  const n = pts.length
  // Top margin holds `Today` above the plot; version labels sit below the axis (I4 §4.2).
  const hasVersions = controls.series === 'score' && planVersions.length > 0
  const m = { top: 24, right: 20, bottom: hasVersions ? 44 : 24, left: 34 }
  const iw = Math.max(80, width - m.left - m.right)
  const ih = height - m.top - m.bottom
  const t0 = pts[0] ? new Date(pts[0].bucket_start).getTime() : bounds.from.getTime()
  const t1 = pts[n - 1] ? new Date(pts[n - 1].bucket_end).getTime() : bounds.to.getTime()
  const span = Math.max(1, t1 - t0)
  const xAt = (iso: string | number) => m.left + (iw * (Math.min(t1, Math.max(t0, typeof iso === 'number' ? iso : new Date(iso).getTime())) - t0)) / span
  const yAt = (v: number) => m.top + ih - (ih * Math.max(0, Math.min(max, v))) / max
  // Each bucket's point sits at the bucket's cut: its end, or now for the live bucket.
  const px = (p: SeriesPoint) => xAt(p.live ? now.getTime() : Math.min(new Date(p.bucket_end).getTime() - 1, now.getTime()))
  const drawn = pts.filter((p) => !p.future)
  const firstSuggested = drawn.findIndex((p) => p.has_suggested)
  const solidPts = firstSuggested < 0 ? drawn : drawn.slice(0, firstSuggested + 1)
  const dashedPts = firstSuggested < 0 ? [] : drawn.slice(Math.max(0, firstSuggested - 1))
  const path = (ps: SeriesPoint[], val: (p: SeriesPoint) => number) => ps.map((p, i) => `${i === 0 ? 'M' : 'L'}${px(p).toFixed(1)},${yAt(val(p)).toFixed(1)}`).join(' ')
  const areaPath = drawn.length > 1 ? `${path(drawn, (p) => p.score)} L${px(drawn[drawn.length - 1]).toFixed(1)},${yAt(0)} L${px(drawn[0]).toFixed(1)},${yAt(0)} Z` : ''
  const milestone = index.submission ? { date: index.submission.deadline, label: index.submission.label.replace(' plan', '') } : index.milestone ? { date: index.milestone.date, label: index.milestone.label } : null
  const msTime = milestone ? new Date(milestone.date).getTime() : null
  const msInside = msTime != null && msTime >= t0 && msTime <= t1
  const daysTo = msTime != null ? Math.ceil((msTime - now.getTime()) / 86_400_000) : null
  const domains = [...index.domains].sort((a, b) => a.order - b.order)
  const domainMax = scale === 'levels' ? 3 : 10
  const yDom = (v: number) => m.top + ih - (ih * Math.max(0, Math.min(domainMax, v))) / domainMax
  const evidenceTicks = sorted.filter((e) => e.event_type === 'evidence_added' && new Date(e.occurred_at).getTime() >= t0 && new Date(e.occurred_at).getTime() <= t1)
  const grouped = evidenceTicks.length > 10 * Math.max(1, n) ? pts.map((p) => ({ x: px(p), count: p.evidence_count })).filter((g) => g.count > 0) : null
  const summary = drawn.length ? `Readiness ${scale === 'levels' ? 'score' : 'index'} ${drawn[0].score} at ${drawn[0].label} to ${drawn[drawn.length - 1].score} now, across ${drawn.length} ${controls.period} buckets, ${series.nodes.length} assessments.${showProjection && projection?.summary.all_l3_by ? ` Projected all objectives at L3 ${fmtMonth(projection.summary.all_l3_by)}.` : ''}` : 'No readiness series yet.'
  const single = drawn.length < 2 && series.nodes.length < 2
  const set = (patch: Partial<ChartControls>) => onControls({ ...controls, ...patch })
  // Plan versions inside the range (I3 §5): squares at created_at, y = completeness on the score axis.
  const versionMarks = planVersions.filter((v) => { const t = new Date(v.created_at).getTime(); return t >= t0 && t <= t1 })

  // ── Projection (I3 §3.2): dotted line from the live point to the estimated
  // L3 date, a translucent P20–P80 cone, a diamond end marker and a hollow
  // `All ≥ L2` waypoint. Never a domain colour.
  const live = drawn[drawn.length - 1]
  const proj = (() => {
    if (!showProjection || !projection || !live) return null
    const s0 = live.score
    const end = projection.summary.all_l3_by
    const nowIso = now.toISOString()
    const dates = Array.from(new Set(projection.objectives.flatMap((o) => o.path))).filter((d) => d > nowIso).sort()
    const val = (d: string) => Math.max(s0, projectedScoreAt(projection, d))
    const pts: Array<{ t: number; v: number }> = [{ t: now.getTime(), v: s0 }, ...dates.map((d) => ({ t: new Date(d).getTime(), v: val(d) }))]
    const endT = end ? new Date(end).getTime() : pts[pts.length - 1].t
    // P20 and P80 in the same month (fewer than three dwell samples): the range is
    // not yet available. No cone is drawn and the L3 pill keeps `range not yet
    // available` (I6 §12, amending I4 §4.2's ±1 quarter display widening).
    const sameMonth = !!projection.summary.p20 && fmtMonth(projection.summary.p20) === fmtMonth(projection.summary.p80)
    const hasRange = !!projection.summary.p20 && !!projection.summary.p80 && !sameMonth
    const p20 = hasRange ? new Date(projection.summary.p20!).getTime() : null
    const p80 = hasRange ? new Date(projection.summary.p80!).getTime() : null
    const scaleT = (t: number, target: number | null) => (target == null || endT <= now.getTime() ? t : now.getTime() + ((t - now.getTime()) * (target - now.getTime())) / (endT - now.getTime()))
    const line = pts.map((p, i) => `${i ? 'L' : 'M'}${xAt(p.t).toFixed(1)},${yAt(p.v).toFixed(1)}`).join(' ')
    const upper = pts.map((p) => ({ x: xAt(scaleT(p.t, p20)), y: yAt(p.v) }))
    const lower = pts.map((p) => ({ x: xAt(scaleT(p.t, p80)), y: yAt(p.v) })).reverse()
    const cone = p20 && p80 ? `M${upper.map((p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' L')} L${lower.map((p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' L')} Z` : ''
    const l2 = projection.summary.all_l2_by && projection.summary.all_l2_by > nowIso ? { x: xAt(projection.summary.all_l2_by), y: yAt(val(projection.summary.all_l2_by)), date: projection.summary.all_l2_by } : null
    const range = sameMonth || !projection.summary.p20 ? 'range not yet available' : `range ${fmtMonth(projection.summary.p20)} – ${fmtMonth(projection.summary.p80)}`
    return { line, cone, hasRange, end: end ? { x: xAt(endT), y: yAt(100), date: end } : null, l2, val, enough: projection.summary.enough_history, range }
  })()
  const msProjected = proj && msTime != null && msTime > now.getTime() ? proj.val(new Date(msTime).toISOString()) : null

  // Coverage (assets known) on its own right axis, with ◆ at scope changes.
  const covInRange = coverage.filter((c) => { const t = new Date(c.date).getTime(); return t >= t0 && t <= Math.min(t1, now.getTime()) })
  const covMax = Math.max(1, ...coverage.map((c) => c.count)) * 1.1
  const yCov = (v: number) => m.top + ih - (ih * Math.max(0, v)) / covMax
  const marks = scopeMarks.filter((sm) => { const t = new Date(sm.date).getTime(); return t >= t0 && t <= t1 })
  const scoreAtDate = (iso: string) => { const p = drawn.find((q) => iso >= q.bucket_start && iso < q.bucket_end); return p ? p.score : live?.score ?? 0 }

  // ── Annotation pills (I4 §4.2): placed, flipped at the edges, stacked. Below
  // 1100px of plot the CSA and L3 pills drop their third segment; below 800px
  // the date line goes too. The full text stays in each pill's tooltip.
  const narrow = iw < 800 ? 2 : iw < 1100 ? 1 : 0
  const lines = (title: string, date: string, extra: string | null) => ({ title, date: narrow >= 2 ? null : narrow === 1 || !extra ? date : `${date} · ${extra}`, full: [title, date, extra].filter(Boolean).join(' · ') })
  const specs: PillSpec[] = []
  if (milestone && msInside) {
    const extra = [daysTo != null && daysTo > 0 ? `${daysTo} d` : null, msProjected != null ? `est. ${msProjected}` : null].filter(Boolean).join(' · ') || null
    specs.push({ key: 'csa', kind: 'csa', ...lines(milestone.label, formatDate(milestone.date), extra), ax: xAt(msTime!), ay: m.top + 8, side: 'right', testId: 'chart-milestone' })
  }
  if (proj?.end) {
    specs.push(proj.enough
      ? { key: 'l3', kind: 'projection', ...lines(scale === 'levels' ? 'All objectives at L3' : 'Projected end', `est. ${fmtMonth(proj.end.date)}`, proj.range), ax: proj.end.x, ay: proj.end.y - 44, side: 'left', testId: 'chart-end-label' }
      : { key: 'l3', kind: 'projection', ...lines('Not enough history', `prior ${proj.range}`, null), ax: proj.end.x, ay: proj.end.y - 44, side: 'left', testId: 'chart-end-label' })
  }
  if (proj?.l2) specs.push({ key: 'l2', kind: 'projection', ...lines('All ≥ L2', fmtMonth(proj.l2.date), null), ax: proj.l2.x, ay: proj.l2.y + 14, side: 'right' })
  marks.forEach((sm) => {
    const y = controls.series === 'coverage' ? yCov(sm.to) : yAt(scoreAtDate(sm.date))
    specs.push({ key: `scope-${sm.date}`, kind: 'scope', ...lines('Scope expanded', `${sm.from.toLocaleString()} → ${sm.to.toLocaleString()}`, null), ax: xAt(sm.date), ay: y - 14 - pillSize('Scope expanded', narrow >= 2 ? null : 'x').h, side: 'right' })
  })
  const pills = layoutPills(specs, { left: m.left, right: m.left + iw, top: m.top, bottom: m.top + ih })
  // Node value labels move to the other side of the line when a pill covers them.
  const labelY = (x: number, y: number) => (coversPoint(pills, x, y - 12) ? y + 18 : y - 9)

  return (
    <section className="rd-card p-4" aria-label="Readiness over time" data-testid="readiness-over-time">
      {/* Title on its own line so the three control groups stay on one row down to 1024 (I4 §3.2). */}
      {/* Section title (I6 §10): `font.sectionTitle`, sentence case; eyebrows stay on tiles. */}
      <h2 className="rd-section-title mb-2.5">{index.name.replace(`${index.regulator} `, '')} {scale === 'levels' ? 'readiness' : 'preparedness'} over time</h2>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 text-[12.5px]">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <Segmented value={controls.period} options={[['week', 'Week'], ['month', 'Month'], ['quarter', 'Quarter'], ['year', 'Year']]} onChange={(v) => set({ period: v as Period })} label="Period" />
        <Segmented value={controls.range} options={[...(projection ? [['est', 'To est. L3'] as [string, string]] : []), ['3', 'Last 3'], ['6', 'Last 6'], ['12', 'Last 12'], ['all', 'All'], ['custom', 'Custom…']]} onChange={(v) => set({ range: v as RangeKey })} label="Range" />
        {controls.range === 'custom' && (
          <span className="flex items-center gap-1">
            <input type="date" aria-label="From" className="rd-input !w-auto !py-0.5 !text-[11.5px]" value={controls.custom.from} onChange={(e) => set({ custom: { ...controls.custom, from: e.target.value } })} />
            <span>–</span>
            <input type="date" aria-label="To" className="rd-input !w-auto !py-0.5 !text-[11.5px]" value={controls.custom.to} onChange={(e) => set({ custom: { ...controls.custom, to: e.target.value } })} />
          </span>
        )}
        <Segmented value={controls.series} options={[['score', 'Score'], ['domain', 'By domain'], ...(coverage.length ? [['coverage', 'Coverage'] as [string, string]] : [])]} onChange={(v) => set({ series: v as SeriesKey })} label="Series" />
        </div>
      </div>
      {/* Legend (I4 §9e): 11.5 mono, 16px gaps, each swatch drawn as its mark. Its own
          row, so it wraps instead of clipping at the card edge (I6 §12.2). */}
      <div className="mt-2 flex flex-wrap items-center text-[12.5px]">
        <span className="rd-mono flex flex-wrap items-center gap-x-4 gap-y-1 text-[11.5px]" style={{ color: 'var(--rd-text-muted)' }} aria-label="Legend" data-testid="chart-legend">
          <LegendItem mark={<line x1={1} x2={17} y1={6} y2={6} stroke={ACCENT} strokeWidth={2} />}>Confirmed</LegendItem>
          <LegendItem mark={<line x1={1} x2={17} y1={6} y2={6} stroke={ACCENT} strokeWidth={2} strokeDasharray="4 3" />}>Incl. suggested</LegendItem>
          <LegendItem mark={<circle cx={9} cy={6} r={4.5} fill={ACCENT} />}>Assessment</LegendItem>
          <LegendItem mark={<circle cx={9} cy={6} r={4} fill="none" stroke={ACCENT} strokeWidth={2} />}>Live</LegendItem>
          <LegendItem mark={<rect x={8} y={1} width={2} height={10} fill="var(--rd-text-faint)" />}>Evidence</LegendItem>
          {hasVersions && <LegendItem mark={<rect x={4} y={1} width={10} height={10} rx={2} fill="var(--rd-pill-version-fg)" />}>CSA plan version</LegendItem>}
          {showProjection && <LegendItem mark={<line x1={1} x2={17} y1={6} y2={6} stroke={PROJ} strokeWidth={2} strokeDasharray="1.5 4" strokeLinecap="round" />}>Projection</LegendItem>}
          {showProjection && (
            <span style={{ opacity: proj?.hasRange ? 1 : 0.4 }} data-testid="legend-confidence-range">
              <LegendItem mark={<rect x={1} y={2} width={16} height={8} rx={2} fill={PROJ} fillOpacity={0.15} stroke={PROJ} strokeWidth={1} />}>Confidence range (P20–P80){proj?.hasRange ? '' : ' · not yet available'}</LegendItem>
            </span>
          )}
          {compared && <LegendItem mark={<><circle cx={9} cy={6} r={5} fill="none" stroke="var(--rd-pair-ring)" strokeWidth={1.5} /><circle cx={9} cy={6} r={2} fill={ACCENT} /></>}>Compared pair</LegendItem>}
          {marks.length > 0 && <LegendItem mark={<rect x={5} y={2} width={8} height={8} transform="rotate(45 9 6)" fill={COV} />}><span style={{ color: COV }}>Scope expanded</span></LegendItem>}
          <Tooltip text={`Built from the level and evidence log, bucketed by the selected period. Assessment nodes record confirmed levels only; the dashed tail includes suggested levels not yet confirmed.${projection ? ` The projection is ${PROJECTION_EXPLAINER}.` : ''}`} className="!no-underline">
            <Icon name="info" size={14} />
          </Tooltip>
        </span>
      </div>

      <div ref={ref} className="relative mt-3" data-chart>
        <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} role="img" aria-label={summary} onMouseLeave={() => setHover(null)}>
          {/* y-axis */}
          {(controls.series !== 'domain' ? [0, max / 2, max] : scale === 'levels' ? [0, 1, 2, 3] : [0, 5, 10]).map((v) => {
            const y = controls.series !== 'domain' ? yAt(v) : yDom(v)
            return (
              <g key={v}>
                <line x1={m.left} x2={m.left + iw} y1={y} y2={y} stroke="var(--rd-border-subtle)" strokeWidth={1} />
                <text x={m.left - 6} y={y + 3} fontSize={11} textAnchor="end" fill="var(--rd-text-muted)" className="rd-axis">{controls.series === 'domain' && scale === 'levels' ? `L${v}` : v}</text>
              </g>
            )
          })}
          {/* x labels */}
          {pts.map((p, i) => (n <= 8 || i % Math.ceil(n / 8) === 0) && !p.future ? (
            <text key={p.bucket_start} x={xAt(p.bucket_start)} y={m.top + ih + 16} fontSize={11} textAnchor="start" fill="var(--rd-text-muted)" className="rd-axis">{p.label}</text>
          ) : null)}
          {/* Today */}
          <line x1={xAt(now.getTime())} x2={xAt(now.getTime())} y1={m.top} y2={m.top + ih} stroke="var(--rd-text-faint)" strokeWidth={1} strokeDasharray="3 3" />
          <text x={xAt(now.getTime())} y={m.top - 6} fontSize={11} textAnchor="middle" fill="var(--rd-text-muted)" className="rd-axis" data-testid="chart-today">Today</text>
          {/* Milestone */}
          {milestone && (msInside ? (
            <g>
              <line x1={xAt(msTime!)} x2={xAt(msTime!)} y1={m.top} y2={m.top + ih} stroke="var(--rd-pill-csa-fg)" strokeWidth={1} strokeDasharray="4 3" />
            </g>
          ) : (
            <text x={m.left + iw} y={m.top - 6} fontSize={11} textAnchor="end" fill="var(--rd-pill-csa-fg)">{milestone.label} · {formatDate(milestone.date)}{daysTo != null && daysTo > 0 ? ` · ${daysTo} d →` : ''}</text>
          ))}

          {controls.series === 'score' ? (
            <>
              {areaPath && <path d={areaPath} fill={ACCENT} opacity={0.1} />}
              {solidPts.length > 1 && <path d={path(solidPts, (p) => p.score)} fill="none" stroke={ACCENT} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />}
              {dashedPts.length > 1 && <path d={path(dashedPts, (p) => p.score)} fill="none" stroke={ACCENT} strokeWidth={2} strokeDasharray="4 3" strokeLinejoin="round" strokeLinecap="round" />}
              {single && drawn[0] && <circle cx={px(drawn[0])} cy={yAt(drawn[0].score)} r={4} fill={ACCENT} />}
            </>
          ) : (
            domains.map((d) => {
              const col = domainColor(d, theme)
              const dpath = drawn.map((p, i) => `${i === 0 ? 'M' : 'L'}${px(p).toFixed(1)},${yDom(p.by_domain[d.code] ?? 0).toFixed(1)}`).join(' ')
              return (
                <g key={d.code}>
                  {drawn.length > 1 && <path d={dpath} fill="none" stroke={col} strokeWidth={1.75} strokeLinejoin="round" strokeLinecap="round" />}
                  {drawn.length > 0 && <circle cx={px(drawn[drawn.length - 1])} cy={yDom(drawn[drawn.length - 1].by_domain[d.code] ?? 0)} r={3.5} fill={col} stroke={SURFACE} strokeWidth={1.5} />}
                  <text x={m.left + iw + 4} y={yDom(drawn.length ? drawn[drawn.length - 1].by_domain[d.code] ?? 0 : 0) + 3} fontSize={11} fill={col} className="rd-axis">{d.code}</text>
                </g>
              )
            })
          )}

          {/* baseline tint: history vs projection */}
          {showProjection && (
            <g aria-hidden="true">
              <line x1={m.left} x2={xAt(now.getTime())} y1={m.top + ih + 0.5} y2={m.top + ih + 0.5} stroke={ACCENT} strokeOpacity={0.5} strokeWidth={2} />
              <line x1={xAt(now.getTime())} x2={m.left + iw} y1={m.top + ih + 0.5} y2={m.top + ih + 0.5} stroke={PROJ} strokeOpacity={0.6} strokeWidth={2} />
            </g>
          )}
          {proj && (
            <g data-testid="chart-projection">
              {proj.cone && <path d={proj.cone} fill={PROJ} opacity={0.1} data-testid="chart-cone"><title>Likely window for each date. Narrower means more history behind the estimate.</title></path>}
              <path d={proj.line} fill="none" stroke={PROJ} strokeWidth={2} strokeDasharray="1.5 4" strokeLinecap="round" />
              {proj.l2 && (
                <g>
                  <circle cx={proj.l2.x} cy={proj.l2.y} r={4.5} fill={SURFACE} stroke={PROJ} strokeWidth={2} />
                </g>
              )}
              {proj.end && (
                <g data-testid="chart-end-marker">
                  <rect x={proj.end.x - 5} y={proj.end.y - 5} width={10} height={10} transform={`rotate(45 ${proj.end.x} ${proj.end.y})`} fill={PROJ} stroke={SURFACE} strokeWidth={1.5} />
                </g>
              )}
            </g>
          )}
          {controls.series === 'coverage' && covInRange.length > 0 && (
            <g data-testid="chart-coverage">
              <path d={covInRange.map((c, i) => `${i ? 'H' + xAt(c.date).toFixed(1) + ' V' : 'M' + xAt(c.date).toFixed(1) + ','}${yCov(c.count).toFixed(1)}`).join(' ') + ` H${xAt(Math.min(t1, now.getTime())).toFixed(1)}`} fill="none" stroke={COV} strokeWidth={2} />
              {[0, covMax / 2, covMax].map((v) => <text key={v} x={m.left + iw + 4} y={yCov(v) + 3} fontSize={11} fill={COV} className="rd-axis">{Math.round(v).toLocaleString()}</text>)}
            </g>
          )}
          {/* scope changes (I3 §4): one ◆ per scope_expanded sync in the range */}
          {marks.map((sm) => {
            const x = xAt(sm.date)
            const y = controls.series === 'coverage' ? yCov(sm.to) : yAt(scoreAtDate(sm.date))
            return (
              <g key={sm.date} data-testid="chart-scope-mark">
                <rect x={x - 4.5} y={y - 4.5} width={9} height={9} transform={`rotate(45 ${x} ${y})`} fill={COV} stroke={SURFACE} strokeWidth={1.5} />
                <title>Scope expanded · {sm.from.toLocaleString()} → {sm.to.toLocaleString()} assets known · coverage-driven changes are better visibility, not regression</title>
              </g>
            )
          })}

          {/* plan versions (I3 §5, I5 §9): a fixed square on the baseline at the
              submission date, labelled `CSA plan v{n}` below the axis; completeness
              lives in the tooltip. Click opens the version's submission record. */}
          {controls.series === 'score' && versionMarks.map((v) => {
            const x = xAt(v.created_at)
            const label = v.label
            const { w, h } = pillSize(label)
            const onEnter = (e: React.MouseEvent<SVGGElement>) => {
              const host = (e.currentTarget as SVGGElement).closest('[data-chart]') as HTMLElement | null
              const r = host?.getBoundingClientRect()
              setVersionHover({ v, x: x, y: m.top + ih - (r ? 0 : 0) })
            }
            return (
              <g key={v.id} data-testid={`chart-version-${v.id}`} className={onOpenVersion ? 'cursor-pointer' : undefined} role={onOpenVersion ? 'link' : undefined} tabIndex={onOpenVersion ? 0 : undefined} aria-label={`${label} · submitted ${formatDate(v.created_at)}`} onMouseEnter={onEnter} onMouseLeave={() => setVersionHover(null)} onFocus={onEnter as unknown as React.FocusEventHandler<SVGGElement>} onBlur={() => setVersionHover(null)} onClick={() => v.n != null && onOpenVersion?.(v.n)} onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && v.n != null) { e.preventDefault(); onOpenVersion?.(v.n) } }}>
                <rect x={x - 5} y={m.top + ih - 5} width={10} height={10} rx={2} fill="var(--rd-pill-version-fg)" stroke={SURFACE} strokeWidth={1.5} />
                <SvgPill p={{ key: v.id, kind: 'version', title: label, ax: x, ay: 0, side: 'right', x: Math.max(m.left, Math.min(m.left + iw - w, x - w / 2)), y: height - h - 2, w, h, full: undefined }} />
              </g>
            )
          })}
          {/* evidence ticks along the baseline */}
          {grouped
            ? grouped.map((g, i) => (
                <g key={i}>
                  <rect x={g.x - 1} y={m.top + ih - 6} width={2} height={6} fill="var(--rd-text-faint)" />
                  <text x={g.x + 3} y={m.top + ih - 1} fontSize={11} fill="var(--rd-text-muted)" className="rd-axis">×{g.count}</text>
                </g>
              ))
            : evidenceTicks.map((e) => <rect key={e.id} x={xAt(e.occurred_at) - 1} y={m.top + ih - 6} width={2} height={6} fill="var(--rd-text-faint)" />)}

          {/* annotation pills (I4 §4.2), above the marks */}
          {pills.map((pl) => <SvgPill key={pl.key} p={pl} />)}

          {/* hover hit targets per bucket */}
          {pts.map((p, i) => !p.future ? (
            <rect
              key={`hit-${p.bucket_start}`}
              x={xAt(p.bucket_start)}
              y={m.top}
              width={Math.max(2, xAt(p.live ? now.getTime() : p.bucket_end) - xAt(p.bucket_start))}
              height={ih}
              fill="transparent"
              onMouseMove={(e) => {
                const host = (e.currentTarget as SVGRectElement).closest('[data-chart]') as HTMLElement | null
                const r = host?.getBoundingClientRect()
                setHover({ i, x: e.clientX - (r?.left ?? 0), y: e.clientY - (r?.top ?? 0) })
              }}
            />
          ) : null)}
          {/* Assessment nodes and the live point sit above the hit targets so a click reaches them (I6 §2.2). */}
          {controls.series === 'score' && (
            <>
            {/* assessment nodes */}
            {series.nodes.map((nd) => (
              <g key={nd.id} className="cursor-pointer" onClick={() => onSelectNode?.(nd.id)} data-testid={`chart-node-${nd.id}`} data-compared={compared && (compared.fromId === nd.id || compared.toId === nd.id) ? 'true' : undefined}>
                {compared && (compared.fromId === nd.id || compared.toId === nd.id) && <circle cx={xAt(nd.created_at)} cy={yAt(nd.score)} r={8} fill="none" stroke="var(--rd-pair-ring)" strokeWidth={1.5} data-testid="chart-pair-ring" />}
                <circle cx={xAt(nd.created_at)} cy={yAt(nd.score)} r={5} fill={ACCENT} stroke={SURFACE} strokeWidth={2} />
                <text x={xAt(nd.created_at)} y={labelY(xAt(nd.created_at), yAt(nd.score))} fontSize={11} fontWeight={600} textAnchor="middle" fill="var(--rd-text-primary)" style={{ fontVariantNumeric: 'tabular-nums' }}>{nd.score}</text>
                <title>{nd.name} · {formatDate(nd.created_at)} · {nd.score}</title>
              </g>
            ))}
            {/* live point */}
            {drawn.length > 0 && (() => {
              const lp = drawn[drawn.length - 1]
              const liveCompared = !!compared && (compared.fromId === 'live' || compared.toId === 'live')
              return (
                <g className={onSelectLive ? 'cursor-pointer' : undefined} onClick={() => onSelectLive?.()} data-testid="chart-node-live" data-compared={liveCompared ? 'true' : undefined}>
                  {liveCompared && <circle cx={px(lp)} cy={yAt(lp.score)} r={8} fill="none" stroke="var(--rd-pair-ring)" strokeWidth={1.5} data-testid="chart-pair-ring" />}
                  <circle cx={px(lp)} cy={yAt(lp.score)} r={5} fill={SURFACE} stroke={ACCENT} strokeWidth={2} />
                  <text x={px(lp)} y={labelY(px(lp), yAt(lp.score))} fontSize={11} fontWeight={600} textAnchor="middle" fill="var(--rd-text-primary)">{lp.score}</text>
                </g>
              )
            })()}
            </>
          )}
        </svg>
        {versionHover && (
          <div className="rd-tooltip" role="tooltip" data-testid="chart-version-tooltip" style={{ position: 'absolute', left: Math.min(Math.max(0, versionHover.x - 120), Math.max(0, width - 300)), top: Math.max(0, versionHover.y - 16), transform: 'translateY(-100%)', zIndex: 30, maxWidth: 300 }}>
            <p className="font-semibold">CSA submission plan · v{versionHover.v.n ?? '—'}</p>
            <p style={{ color: 'var(--rd-text-muted)' }}>Submitted {formatDate(versionHover.v.created_at)}{versionHover.v.submitted_by ? ` by ${versionHover.v.submitted_by}` : ''}{versionHover.v.csa_reference ? ` · CSA ref ${versionHover.v.csa_reference}` : ''} · plan {versionHover.v.completeness}% complete</p>
          </div>
        )}
        {hover && pts[hover.i] && (() => {
          const p = pts[hover.i]
          const prev = hover.i > 0 ? pts[hover.i - 1] : null
          const d = prev ? Math.round((p.score - prev.score) * 10) / 10 : null
          return (
            <div className="rd-tooltip" role="status" style={{ position: 'absolute', left: Math.min(hover.x + 12, Math.max(0, width - 260)), top: Math.max(0, hover.y - 8), transform: 'translateY(-100%)', zIndex: 30 }}>
              <p className="font-semibold">{p.label}{p.live ? ' · live' : ''}</p>
              <p style={{ color: 'var(--rd-text-muted)' }}>{formatDate(p.bucket_start)} – {formatDate(p.live ? now.toISOString() : new Date(new Date(p.bucket_end).getTime() - 1).toISOString())}</p>
              <p className="mt-1"><span className="rd-mono font-semibold">{p.score}</span>{d != null ? <span style={{ color: d > 0 ? 'var(--rd-status-ok-text)' : d < 0 ? 'var(--rd-status-warn)' : 'var(--rd-text-muted)' }}> · {d > 0 ? '▲' : d < 0 ? '▼' : '='} {Math.abs(d)}</span> : null}{p.has_suggested ? <span style={{ color: 'var(--rd-status-warn)' }}> · incl. suggested ({p.score_confirmed_only} confirmed)</span> : null}</p>
              {p.moved.length > 0 && <p style={{ color: 'var(--rd-text-muted)' }}>Moved: {p.moved.map((mv) => `${mv.code} ${scale === 'levels' ? `${mv.from}→${mv.to}` : `${mv.from_score ?? '—'}→${mv.to_score ?? '—'}`}`).join(', ')}</p>}
              <p style={{ color: 'var(--rd-text-muted)' }}>Evidence added: {p.evidence_count}</p>
            </div>
          )
        })()}
        {single && (
          <p className="pointer-events-none absolute inset-x-0 top-1/2 -translate-y-1/2 text-center text-[12.5px]" style={{ color: 'var(--rd-text-muted)' }}>
            Tracking started {series.started_at ? formatDate(series.started_at) : 'today'}. The chart fills in as levels and evidence change.
          </p>
        )}
      </div>
    </section>
  )
}

function LegendItem({ mark, children }: { mark: ReactNode; children: ReactNode }) {
  return (
    <span className="flex items-center gap-1.5 whitespace-nowrap">
      <svg width={18} height={12} viewBox="0 0 18 12" aria-hidden="true">{mark}</svg>
      {children}
    </span>
  )
}

function Segmented({ value, options, onChange, label }: { value: string; options: Array<[string, string]>; onChange: (v: string) => void; label: string }) {
  return (
    <span className="rd-segmented" role="radiogroup" aria-label={label}>
      {options.map(([v, l]) => (
        <button key={v} type="button" role="radio" aria-checked={value === v} onClick={() => onChange(v)}>
          {l}
        </button>
      ))}
    </span>
  )
}
